import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import useSEO from "@/lib/useSEO";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowUpRight, Globe, MapPin, Factory, Building2, ShieldCheck, Info } from "lucide-react";

const SITE = "https://spa-report-hub.emergent.host";

export default function ManufacturerDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api
      .get(`/manufacturers/${slug}`)
      .then((r) => setM(r.data))
      .catch((e) => {
        if (e.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  useSEO(
    m
      ? {
          title: `${m.name} — Fabricant de spas${m.country ? " (" + m.country + ")" : ""} | DoctoSpa`,
          description: `${m.name}${m.group_name && m.group_name !== m.name ? " (groupe " + m.group_name + ")" : ""} : ${m.products || "fabricant de spas"}${m.country ? " basé au " + m.country : ""}. Positionnement ${m.positioning || "N/C"}. ${m.notes || ""}`.slice(0, 300),
          url: `${SITE}/annuaire-fabricants/${m.slug}`,
          type: "article",
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: m.name,
            url: m.website || `${SITE}/annuaire-fabricants/${m.slug}`,
            address: m.country ? { "@type": "PostalAddress", addressCountry: m.country } : undefined,
            parentOrganization: m.group_name && m.group_name !== m.name ? { "@type": "Organization", name: m.group_name } : undefined,
          },
        }
      : { title: "Fabricant — DoctoSpa" }
  );

  if (loading) return <div className="mx-auto max-w-3xl px-6 py-24 text-sm text-muted-foreground">Chargement…</div>;
  if (notFound || !m) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-24 text-center" data-testid="manufacturer-not-found">
        <h1 className="font-display text-2xl font-bold">Fabricant introuvable</h1>
        <Button className="mt-6 rounded-full" onClick={() => navigate("/annuaire-fabricants")} data-testid="manufacturer-back-btn">
          Retour à l'annuaire
        </Button>
      </div>
    );
  }

  const rows = [
    { icon: Building2, label: "Groupe", value: m.group_name && m.group_name !== m.name ? m.group_name : null },
    { icon: MapPin, label: "Pays d'origine", value: m.country },
    { icon: Factory, label: "Zone de fabrication", value: m.manufacturing_zone },
    { icon: Info, label: "Produits", value: m.products },
    { icon: ShieldCheck, label: "Statut", value: m.status },
  ].filter((r) => r.value);

  return (
    <article className="mx-auto max-w-4xl px-6 lg:px-10 py-12 lg:py-16" data-testid="manufacturer-detail-page">
      <Link to="/annuaire-fabricants" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="manufacturer-back-link">
        <ArrowLeft className="h-4 w-4" /> Annuaire fabricants
      </Link>

      <div className="mt-8 flex flex-wrap items-start justify-between gap-6">
        <div>
          <div className="flex flex-wrap gap-2">
            {m.positioning && <Badge variant="secondary" className="rounded-full" data-testid="manufacturer-positioning">{m.positioning}</Badge>}
            {m.oem?.toLowerCase().startsWith("oui") && <Badge className="rounded-full bg-amber-100 text-amber-900 hover:bg-amber-100" data-testid="manufacturer-oem">OEM / ODM</Badge>}
            {m.europe_presence === "Oui" && <Badge className="rounded-full bg-black/10 text-foreground hover:bg-black/10" data-testid="manufacturer-europe">Distribué en Europe</Badge>}
            {m.confidence && <Badge variant="outline" className="rounded-full" data-testid="manufacturer-confidence">Confiance : {m.confidence}</Badge>}
          </div>
          <h1 className="mt-4 font-display text-4xl lg:text-5xl font-black tracking-tighter" data-testid="manufacturer-name">{m.name}</h1>
          {m.country && <p className="mt-2 text-muted-foreground inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{m.country}</p>}
        </div>

        {m.website && (
          <a href={m.website} target="_blank" rel="noopener noreferrer nofollow" data-testid="manufacturer-website-btn">
            <Button className="h-11 px-5 rounded-full bg-primary hover:bg-primary/90">
              <Globe className="h-4 w-4 mr-2" /> Site officiel <ArrowUpRight className="h-3 w-3 ml-1" />
            </Button>
          </a>
        )}
      </div>

      {/* Notes */}
      {m.notes && (
        <Card className="mt-10 p-6 border-black/10 bg-primary/5" data-testid="manufacturer-notes">
          <div className="eyebrow">Informations</div>
          <p className="mt-2 text-sm leading-relaxed">{m.notes}</p>
        </Card>
      )}

      {/* Data grid */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="manufacturer-data-grid">
        {rows.map((r) => (
          <Card key={r.label} className="p-5 border-black/10">
            <div className="flex items-center gap-2 text-xs text-muted-foreground font-semibold uppercase tracking-wider">
              <r.icon className="h-3.5 w-3.5" /> {r.label}
            </div>
            <div className="mt-2 text-sm font-medium">{r.value}</div>
          </Card>
        ))}
      </div>

      {/* Website link */}
      {m.website && (
        <div className="mt-8">
          <div className="eyebrow">Site officiel</div>
          <a
            href={m.website}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="mt-2 inline-flex items-center gap-2 text-primary font-semibold hover:underline"
            data-testid="manufacturer-website-link"
          >
            <Globe className="h-4 w-4" />
            {m.website.replace(/^https?:\/\//, "")}
            <ArrowUpRight className="h-3 w-3" />
          </a>
        </div>
      )}

      {/* Siblings (marques du même groupe) */}
      {m.siblings?.length > 0 && (
        <div className="mt-14">
          <div className="eyebrow">Autres marques du groupe</div>
          <h3 className="mt-3 font-display text-2xl font-bold">{m.group_name}</h3>
          <div className="mt-6 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {m.siblings.map((s) => (
              <Link key={s.id} to={`/annuaire-fabricants/${s.slug}`} className="group" data-testid={`manufacturer-sibling-${s.slug}`}>
                <Card className="p-4 border-black/10 hover:border-primary/40 hover:shadow-md transition-all">
                  <div className="font-semibold text-sm group-hover:text-primary transition-colors">{s.name}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{s.country} · {s.positioning}</div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="mt-16 rounded-2xl bg-secondary text-white p-8">
        <div className="eyebrow text-white/60">Une panne sur un spa {m.name} ?</div>
        <h3 className="mt-2 font-display text-2xl font-bold tracking-tight">Lancez un diagnostic gratuit en 2 min</h3>
        <p className="mt-2 text-sm text-white/70">
          Notre IA identifie 27 pannes classiques et vous propose un rapport détaillé + un technicien près de chez vous.
        </p>
        <Link to="/diagnostic">
          <Button className="mt-6 bg-primary hover:bg-primary/90 rounded-full h-11 px-6" data-testid="manufacturer-cta-diagnostic">
            Démarrer le diagnostic
          </Button>
        </Link>
      </div>
    </article>
  );
}
